import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Camera, Package as PackageIcon, X, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Mobile-first barcode/QR scanner for warehouse staff.
 *
 * Flow:
 *  1. Opens the device camera (back-facing on mobile, regular on desktop).
 *  2. Decodes the tracking number from the scanned barcode/QR.
 *  3. Calls GET /api/packages?search=<tracking> to find the package.
 *  4. Shows package + quick actions: Mark Received / Ready for Pickup / Open.
 *  5. After an action, auto-resumes scanning so multiple packages can be
 *     processed back-to-back without re-clicking the camera button.
 */
export default function BarcodeScannerDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const containerId = "kanal-barcode-scanner";
  const scannerRef = useRef(null);
  const [scanState, setScanState] = useState("idle"); // idle | scanning | searching | found | not_found | error
  const [matchedPkg, setMatchedPkg] = useState(null);
  const [lastError, setLastError] = useState("");
  const [acting, setActing] = useState(false);

  const stopScanner = async () => {
    const s = scannerRef.current;
    if (s) {
      try { await s.stop(); } catch (_) { /* may already be stopped */ }
      try { s.clear(); } catch (_) { /* ignore */ }
      scannerRef.current = null;
    }
  };

  const startScanner = async () => {
    setMatchedPkg(null);
    setLastError("");
    setScanState("scanning");
    // Dynamic import — keeps the html5-qrcode library out of the main bundle
    const { Html5Qrcode } = await import("html5-qrcode");
    await stopScanner(); // safety
    const scanner = new Html5Qrcode(containerId, { verbose: false });
    scannerRef.current = scanner;
    try {
      await scanner.start(
        { facingMode: "environment" }, // back camera on mobile
        {
          fps: 8,
          qrbox: { width: 260, height: 160 },
          aspectRatio: 1.5,
        },
        async (decoded) => {
          // Successful decode — pause and look the package up
          const tracking = (decoded || "").trim();
          if (!tracking) return;
          await stopScanner();
          await lookup(tracking);
        },
        () => { /* per-frame scan failures: ignore quietly */ }
      );
    } catch (e) {
      console.error("Scanner start failed", e);
      setLastError(e?.message || "Could not open camera. Allow camera permissions and retry.");
      setScanState("error");
    }
  };

  const lookup = async (tracking) => {
    setScanState("searching");
    try {
      const { data } = await api.get("/packages", { params: { search: tracking, page_size: 1 } });
      const items = data?.items || [];
      if (items.length === 0) {
        setMatchedPkg({ tracking_number: tracking });
        setScanState("not_found");
        return;
      }
      setMatchedPkg(items[0]);
      setScanState("found");
    } catch (e) {
      setLastError(formatApiErrorDetail(e.response?.data?.detail) || "Lookup failed");
      setScanState("error");
    }
  };

  const updateStatus = async (newStatus) => {
    if (!matchedPkg?.id) return;
    setActing(true);
    try {
      await api.patch(`/packages/${matchedPkg.id}`, { status: newStatus });
      toast.success(`Package ${matchedPkg.tracking_number} → ${newStatus.replace("_", " ")}`);
      // Auto-resume scanning so the next package can be scanned immediately
      setMatchedPkg(null);
      await startScanner();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Status update failed");
    } finally {
      setActing(false);
    }
  };

  const openDetail = () => {
    if (!matchedPkg?.id) return;
    onOpenChange(false);
    navigate(`/packages/${matchedPkg.id}`);
  };

  // Auto-start on open, cleanup on close
  useEffect(() => {
    if (open) {
      startScanner();
    } else {
      stopScanner();
      setScanState("idle");
      setMatchedPkg(null);
    }
    return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-1rem)] max-w-md p-0 gap-0 overflow-hidden"
        data-testid="barcode-scanner-dialog"
      >
        <DialogHeader className="px-5 pt-4 pb-3 border-b border-zinc-200">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Camera size={18} className="text-[#002BF6]" weight="bold" />
            Scan tracking barcode
          </DialogTitle>
        </DialogHeader>

        {/* Camera viewport */}
        <div className="relative bg-black aspect-[3/2]">
          <div id={containerId} className="absolute inset-0" data-testid="barcode-scanner-video" />
          {scanState === "searching" && (
            <div className="absolute inset-0 bg-black/70 flex items-center justify-center text-white text-sm">
              <ArrowsClockwise size={20} className="animate-spin mr-2" />
              Looking up…
            </div>
          )}
          {scanState === "error" && (
            <div className="absolute inset-0 bg-red-950/90 flex flex-col items-center justify-center text-white text-sm px-6 text-center">
              <X size={24} className="mb-2" weight="bold" />
              {lastError}
              <Button
                onClick={startScanner}
                size="sm"
                variant="outline"
                className="mt-3 bg-white text-zinc-900"
                data-testid="scanner-retry-btn"
              >
                Try again
              </Button>
            </div>
          )}
        </div>

        {/* Result panel */}
        <div className="p-4 bg-white">
          {scanState === "scanning" && (
            <div className="text-center text-xs text-zinc-500">
              Hold the barcode steady inside the frame
            </div>
          )}

          {scanState === "not_found" && matchedPkg && (
            <div className="text-center py-2" data-testid="scanner-not-found">
              <X size={22} className="mx-auto mb-1.5 text-red-500" weight="bold" />
              <div className="font-semibold text-sm text-zinc-900">No match for {matchedPkg.tracking_number}</div>
              <div className="text-xs text-zinc-500 mt-0.5">Check the barcode or add the package manually.</div>
              <Button onClick={startScanner} size="sm" className="mt-3 bg-[#002BF6] text-white hover:bg-[#0021C7]" data-testid="scanner-rescan-btn">
                <ArrowsClockwise size={13} className="mr-1.5" /> Scan again
              </Button>
            </div>
          )}

          {scanState === "found" && matchedPkg && (
            <div data-testid="scanner-found">
              <div className="flex items-start gap-2.5 mb-3">
                <CheckCircle size={20} className="text-emerald-600 mt-0.5 shrink-0" weight="fill" />
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-xs font-semibold text-zinc-900">{matchedPkg.tracking_number}</div>
                  <div className="text-sm text-zinc-700 truncate">{matchedPkg.recipient_name}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{matchedPkg.recipient_address}</div>
                  <div className="text-[11px] mt-1">
                    <span className="font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700">
                      {matchedPkg.status}
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <Button
                  onClick={() => updateStatus("in_transit")}
                  disabled={acting || matchedPkg.status === "in_transit"}
                  size="sm"
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="scanner-mark-received-btn"
                >
                  <PackageIcon size={13} className="mr-1.5" weight="bold" /> Received
                </Button>
                <Button
                  onClick={() => updateStatus("ready_for_pickup")}
                  disabled={acting || matchedPkg.status === "ready_for_pickup"}
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="scanner-mark-ready-btn"
                >
                  <CheckCircle size={13} className="mr-1.5" weight="bold" /> Ready for pickup
                </Button>
              </div>
              <div className="flex gap-2 mt-2">
                <Button onClick={openDetail} variant="outline" size="sm" className="flex-1" data-testid="scanner-open-detail-btn">
                  Open detail
                </Button>
                <Button onClick={startScanner} variant="ghost" size="sm" className="flex-1" data-testid="scanner-rescan-btn">
                  <ArrowsClockwise size={13} className="mr-1.5" /> Scan next
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
